import { Component, DetailEvent, Entity, registerComponent } from "aframe";
import { Object3D } from "three";
import { LocationSystem } from "../location";
import { AvatarSystem } from "../avatar";
import { OfflineChannelComponent } from "./offline-channel";
import { waitForParentsLoaded } from "../utils";

/*

offline/standalone equivalent of rtc-gateway
= a gateway to an offline-channel

*/


const waitForComponentLocation = (el : Entity, comp : Component) =>
	new Promise<void>( (resolve, reject) =>
	{
		const listener = ((event : DetailEvent<Component>) =>
		{
			if(event.detail !== comp) return;
			el.removeEventListener("location-initialized", listener);
			resolve();
		}) as EventListener;
		el.addEventListener("location-initialized", listener);
	} );

export async function waitForEntityLocation(el : Entity)
{
	while( el )
	{
		const promises : Promise<void>[] = [];
		for(let key in el.components)
		{
			let comp : Component & { locationPending ? : string } = el.components[key];
			if(comp.locationPending)
			{
				console.log("Waiting for pending location from", comp.name, ":", comp.locationPending);
				promises.push(waitForComponentLocation(el, comp));
			}
		}
		if(promises.length)
		{
			await Promise.all(promises);
			console.log("Location ready");
		}
	
		let obj : Object3D & { el? : Entity } = el.object3D;
		while(obj = obj.parent!)
			if(obj.el && (obj.el !== el))
				break;
		el = obj?.el!;
	}
}


export interface GatewayComponentData
{
	name : string;
	joined : string;
	target : string;
}

interface OfflineGatewayComponent extends Component
{
	data : GatewayComponentData & {
		template : string;
	};
	getChannel() : Entity;
}

registerComponent("offline-gateway", {

	schema : {
		name : { type : "string" },
		template : { type : "string" },
		joined : { type : "string" },
		target : { type : "string" }
	},

	
	getChannel(this : OfflineGatewayComponent)
	{
		const channels = this.el.sceneEl!.querySelectorAll<Entity>("[offline-channel]");
		for(let channel of Array.from(channels))
		{
			const channelData = (channel.components["offline-channel"] as OfflineChannelComponent).data
			if(channelData && (channelData.template == this.data.template))
				return channel;
		}
	},

	init(this : OfflineGatewayComponent)
	{
		const channel = this.getChannel();

		if(!this.data.joined)
		{
			const joined = Boolean(channel && channel.classList.contains("joined"));
			this.el.setAttribute(this.name, { joined : JSON.stringify(joined) });
		}
	},

	update(this : OfflineGatewayComponent, oldData : GatewayComponentData)
	{
		if(this.data.name && oldData && (oldData.name != this.data.name))
			this.el.emit("gateway-initialized", this.data.name);

		if(!this.data.joined) return; //Not wanting anything
		if(this.data.joined == oldData.joined) return; //Already joining/leaving.

		const shouldJoin = Boolean(JSON.parse(this.data.joined));
		let channel = this.getChannel();
		const joined = Boolean(channel && channel.classList.contains("joined"));

		if(joined == shouldJoin) return; //Already good!

		const scene = this.el.sceneEl!;
		if(shouldJoin)
		{
			if(!channel)
			{
				channel = document.createElement("a-entity") as Entity;
				channel.setAttribute("offline-channel", { template : this.data.template } );
				scene.append(channel);
			}

			const targetName = this.data.target;
				
			(this.el.sceneEl!.systems.location as LocationSystem).addChannel( this.data.template, targetName );

			if(!targetName)
			{
				//No target : don't leave, don't teleport, just join
				channel.classList.add("joined");
				channel.emit("channel-joined", channel.components["offline-channel"]);
				return;
			}

			const avatarEl = (scene.systems.avatar as AvatarSystem).avatar;

			//Transfer physics responsibility to avatar itself :
			avatarEl.emit("enter-gateway", this);
		
			//if(avatarEl.components["ammo-body"])
				//avatarEl.setAttribute("ammo-body", { enabled : false });
			
			const gwInited = ((event : DetailEvent<string>) =>
			{
				if(event.detail != targetName) return;

				console.log("Gateway found :", targetName);

				channel.removeEventListener("gateway-initialized", gwInited);
				channel.classList.add("joined");
				event.target.emit("channel-joined", channel.components["offline-channel"]);

				waitForEntityLocation(event.target).then( () =>
				{
					event.target.emit("teleport-avatar");

					avatarEl.emit("exit-gateway", this);

					//if(avatarEl.components["ammo-body"])
						//avatarEl.setAttribute("ammo-body", { enabled : true });
				});
			}) as EventListener;
			channel.addEventListener("gateway-initialized", gwInited);

			waitForParentsLoaded(this.el).then( (e) => e.emit("leave-channel") );
		}
		else if(channel)
		{
			channel.classList.remove("joined");
			channel.remove();
		}
	}
});